// Forgerock Types
// extracted from @forgerock/javascript-sdk-ui (https://github.com/ForgeRock/forgerock-javascript-sdk)

import Cookies from 'cookies-ts';
import router from '@/routes';
import { useLoginStore } from '@/components/login.store';
import * as forgerock from '@forgerock/javascript-sdk';
import { CallbackType, FRAuth, FRLoginFailure, FRLoginSuccess, FRStep, StepType } from '@forgerock/javascript-sdk';

const APP_CONFIG = window.APP_CONFIG;

export const amDomain = APP_CONFIG.forgerock.amBase;
const amBaseURL = `${amDomain}${import.meta.env.VITE_AM_PATH}`;
console.info('[forgerock] amBaseURL:', amBaseURL);

/**
 * configure the forgerock-sdk
 */
forgerock.Config.set({
  serverConfig: {
    baseUrl: amBaseURL,
    timeout: APP_CONFIG.forgerock?.timeout ?? 3000,
  },
  realmPath: import.meta.env.VITE_REALM_PATH,
  logLevel: APP_CONFIG.forgerock?.logLevel ?? 'info',
});

const toJson = async (resp: Response) => {
  if (!resp?.ok) throw resp;
  return resp.json();
};

export interface FrSessionInfo {
  username: string,
  universalId: string,
  realm: string,  //	"/consumer"
  latestAccessTime: Date,         //	"2024-03-25T14:44:13Z"
  maxIdleExpirationTime: Date     //	"2024-03-25T18:44:13Z"
  maxSessionExpirationTime: Date  //	"2024-03-25T22:44:12Z"
  properties: { AMCtxId: string }
}

const cookies = new Cookies();

export const fr = new class Forgerock {

  /**
   * called when the app starts to start the authentication process
   */
  async initAuthentication() {

    const query = Object.fromEntries(new URLSearchParams(location.search));
    let firstStep = undefined;

    // first check for "reentry" cookie (set by FR before starting the federation to know how to return)
    const reentry = cookies.get('reentry');
    if (reentry) {
      console.debug('found reentry: ', reentry);
      cookies.remove('reentry'); // consume it

      const authId = sessionStorage.getItem(reentry);
      console.debug('found authId:', authId);
      sessionStorage.removeItem(reentry);

      if (authId) {
        firstStep = new FRStep({ authId });
      }

      // on reentry we need to remove these - or we get 401
      delete query.authIndexType;
      delete query.authIndexValue;

      console.debug('[SUI:FR] returning to auth-three...');
    } else {
      // expand service param (for confirm-registration)
      if (query.service && !(query.hasOwnProperty('authIndexType') && query.hasOwnProperty('authIndexValue'))) {
        query.authIndexType = 'service';
        query.authIndexValue = query.service;
      }
      console.debug('[SUI:FR] starting new auth-three...');
    }

    // create the request options
    //const options = this.buildStartRequest();
    const options = {
      redirectUri: query.goto,
      query,
    };

    return FRAuth.next(firstStep, options)
      .then((r) => {
        this.handleNextStep(r, undefined, (err) => {
          console.error(`[SUI:FR] error starting auth-three: ${err}`);

          const params = new URLSearchParams(window.location.search);

          const msg = err.getMessage();

          // handle custom errors possible from this auth-tree
          if (params.get('service') === 'dealer_confirm') {
            if (msg === 'INVALID_TOKEN' || msg === 'ALREADY_CONFIRMED' || msg === 'MISSING_EMAIL' || msg === 'MISSING_TOKEN') {
              return { path: '/error/invalidConfirmationLink' };
            }
          }

          if (msg?.startsWith('$UE:')) {
            // UNRECOVERABLE ERROR - go to error page
            return { path: `/error/${msg.substring(4)}` };
          }

          return { path: '/error' };
        });
      });
  };

  // private buildStartRequest(): forgerock.StepOptions {
  //
  //   //const url = new URL(location.href);
  //
  //   // copy the parameters from the current URL
  //   const query = Object.fromEntries(new URLSearchParams(location.search));
  //
  //   // expand service param
  //   if (query.service && !(query.hasOwnProperty('authIndexType') && query.hasOwnProperty('authIndexValue'))) {
  //     query.authIndexType = 'service';
  //     query.authIndexValue = query.service;
  //   }
  //
  //   // the authIndexType and authIndexValue are only required when starting a specific auth-tree (ex: with acr_value)
  //   // they are to be removed if:
  //   // - dealer_login (default auth-tree)
  //   // - reentry after federation
  //   if ((query.authIndexValue === 'usPortal' && query.acr_values !== 'usa')
  //     || (query.authIndexValue === 'employee' && query.acr_values !== 'employee')) {
  //     delete query.authIndexType;
  //     delete query.authIndexValue;
  //   }
  //
  //   return {
  //     redirectUri: query.goto,
  //     query,
  //   };
  //
  // };

  /**
   * generic step handler
   * @param currentStep step to submit
   * @param customStepHandler if provided it will be called after the standard step handler
   * @param customErrorHandler if provided it will be called after the standard error handler
   */
  submitStep(currentStep: FRStep,
    customStepHandler?: (r: FRStep) => void,
    customErrorHandler?: (r: FRLoginFailure) => void) {

    console.debug(`[SUI:FR] submitting step from stage:${currentStep.getStage()}`);

    FRAuth.next(currentStep).then((r) => this.handleNextStep(r, customStepHandler, customErrorHandler));
  }

  handleNextStep(resp: FRStep | FRLoginSuccess | FRLoginFailure,
    customStepHandler?: (r: FRStep) => void,
    customErrorHandler?: (r: FRLoginFailure) => void) {

    console.debug(`[SUI:FR] handling nextStep: ${resp.type} (stage:${resp.payload.stage})`);

    switch (resp.type) {

      case StepType.LoginSuccess:
        this.handleLoginSuccess(resp);
        break;

      case StepType.Step:
        if (resp.getStage()) {
          this.handleStageStep(resp);
          if (customStepHandler) return customStepHandler(resp);
        } else {
          this.handleRedirectStep(resp);
        }
        break;

      case StepType.LoginFailure:
        const msg = resp.getMessage(); //e.response?.data.message;

        if (msg?.startsWith('$UE:')) {
          // UNRECOVERABLE ERROR - go to error page
          router.push({ path: `/error/${msg.substring(4)}` });
          return;
        }

        if (customErrorHandler) return customErrorHandler(resp);
        break;
    }
  }

  private handleLoginSuccess(resp: FRLoginSuccess) {

    let successUrl = resp.getSuccessUrl();
    if (!successUrl) throw new Error('[SUI:FR] no success-url');

    // FOR LOCAL TESTING: override SuccessUrl
    if (import.meta.env.VITE_REBASE_SUCCESS_URL === 'true') {
      // override the base of the SuccessUrl with localhost which is proxied by vite to correct app
      const url = new URL(successUrl);
      url.protocol = 'http';
      url.host = 'localhost';
      url.port = '3000';

      successUrl = url.href;
      console.warn(`[SUI:DEBUG_MODE] rebased ${resp.getSuccessUrl()} to ${successUrl}`);
    } else if (!!import.meta.env.VITE_OVERRIDE_SUCCESS_URL) {
      successUrl = import.meta.env.VITE_OVERRIDE_SUCCESS_URL;
      console.warn(`[SUI:DEBUG_MODE] replaced ${resp.getSuccessUrl()} with ${successUrl}`);
    }
    if (!successUrl) throw new Error('[SUI:FR] no success-url');

    if (this.isRedirectToCustomProtocol(successUrl)) {
      console.info('[SUI:FR] show post-login page', successUrl);
      router.replace({ path: '/post-login' });
    }

    console.info('[SUI:FR] redirecting to success-url:', successUrl);
    window.location.href = successUrl;
  }

  /**
   * check if the redirect_uri param from successUrl uses a custom protocol (e.g. sdsii://custom)
   */
  private isRedirectToCustomProtocol(successUrl: string): boolean {
    if (successUrl) {
      const url = new URLSearchParams(successUrl);
      const redirectUri = url.get('redirect_uri');
      if (redirectUri && !redirectUri.startsWith('https://') && !redirectUri.startsWith('http://')) {
        return true;
      }
    }
    return false;
  }

  /**
   * a stage step corresponds to a page
   * @param resp
   */
  private handleStageStep(resp: FRStep) {
    const store = useLoginStore();

    // on confirmation page, we need to check if we are already logged in
    if (store.existingSession && resp.getStage() === 'set-pw-page') {
      console.error(`[SUI:FR] already logged in`);
      router.push({ path: '/error/alreadyLoggedIn' }); // move to next page
      return;
    }

    store.step = resp; // save step

    console.info(`[SUI:FR] goto stage:${resp.getStage()}`);
    router.push({ name: resp.getStage() }); // move to next page
  }

  private handleRedirectStep(resp: FRStep) {
    const redirectCBs = resp.getCallbacksOfType(CallbackType.RedirectCallback);
    const redirectCallback = redirectCBs && redirectCBs.length > 0 ? redirectCBs[0] : undefined;
    if (redirectCallback) {
      //console.debug("[SUI:FR] redirectCallback", redirectCallback)
      //const output = toObject(redirectCallback.output);
      const authId = resp.payload.authId;

      if (redirectCallback.getOutputByName('trackingCookie', '') && authId) {
        // before performing the redirect, if "trackingCookie" is set,
        // we save the "authId" in the sessionStore under the key specified by the "reentry" cookie
        // on return the authId will be sent in the body
        const cookies = new Cookies();
        const reentry = cookies.get('reentry');
        if (reentry) {
          //console.debug("saving authId as ", reentry)
          sessionStorage.setItem(reentry, authId);
        } else {
          console.warn('[SUI:FR] no reentry cookie');
        }
      } else {
        console.warn('[SUI:FR] no trackingCookie to set');
      }

      if (redirectCallback.getOutputByName<string>('redirectMethod', '') === 'GET') {
        location.href = redirectCallback.getOutputByName<string>('redirectUrl', '');
        return;
      } else {
        throw new Error('Not supported redirect method');
      }
    }
    console.error(`[SUI:FR] unexpected step`, resp);
    throw new Error('[SUI:FR] unexpected step');
  }

  async logout() {
    return fetch(`${amBaseURL}/json/sessions/?_action=logout`, {
      method: 'POST',
      headers: {
        'Accept-API-Version': 'resource=3.1, protocol=1.0',
        'Content-Type': 'application/json',
      }
    });
  }

  async getSessionInfo(): Promise<FrSessionInfo> {
    return fetch(`${amBaseURL}/json/sessions/?_action=getSessionInfo`, {
      method: 'POST',
      headers: {
        'Accept-API-Version': 'protocol=1.0,resource=2.0',
        'Content-Type': 'application/json',
      }
    }).then(toJson);
  };

  async setPropsOnSession(props: Record<string, string>): Promise<Record<string, string>> {
    console.debug('[SUI:FR] setPropsOnSession', props);
    return fetch(`${amBaseURL}/json/consumer/sessions/?_action=updateSessionProperties`, {
      method: 'POST',
      headers: {
        'Accept-API-Version': 'resource=3.1, protocol=1.0',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(props)
    }).then(toJson);
  };
};
